/**
 * Voice / calling settings — typed wrapper around operations.
 *
 * Agent flags are operations-native. Automation / receptionist / IVR are proxied
 * by operations to the calling service (CS); those responses use the CS envelope
 * `{ success, data }`, which we unwrap here so callers get the payload directly.
 */

import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  operationsClient,
} from '@/lib/api';
import type { CallFlow, UpsertCallFlowPayload } from './call-flow-types';
import type {
  AccountCallSchedule,
  AssignableNumber,
  AgentVoiceFlags,
  Automation,
  CreateLandlinePayload,
  CsEnvelope,
  DirectoryEntry,
  IvrAnalytics,
  IvrTree,
  Landline,
  LandlineCallerId,
  LandlineRegistration,
  LandlineRingScope,
  NumberRouting,
  ReceptionistConfig,
  UpdateAccountSchedulePayload,
  UpdateAutomationPayload,
  UpdateNumberRoutingPayload,
  UpdateReceptionistPayload,
  UpsertDirectoryPayload,
  UpsertIvrPayload,
  VoiceAgent,
  VoiceAgentsResponse,
} from './types';

const url = {
  accountSchedule: () => '/api/voice/phone-settings/schedule',
  agentSettings: () => '/api/voice/agent-settings',
  agentSetting: (userId: string) => `/api/voice/agent-settings/${userId}`,
  automation: () => '/api/voice/automation',
  receptionist: () => '/api/voice/receptionist',
  ivr: () => '/api/voice/ivr',
  ivrId: (id: string) => `/api/voice/ivr/${id}`,
  ivrActivate: (id: string) => `/api/voice/ivr/${id}/activate`,
  ivrDeactivate: (id: string) => `/api/voice/ivr/${id}/deactivate`,
  ivrAnalytics: () => '/api/voice/ivr/analytics',
  callFlows: () => '/api/voice/call-flows',
  callFlow: (id: string) => `/api/voice/call-flows/${id}`,
  callFlowStep: (id: string, stepId: string) =>
    `/api/voice/call-flows/${id}/steps/${encodeURIComponent(stepId)}`,
  callFlowPublish: (id: string) => `/api/voice/call-flows/${id}/publish`,
  callFlowUnpublish: (id: string) => `/api/voice/call-flows/${id}/unpublish`,
  callFlowSchedule: (id: string) => `/api/voice/call-flows/${id}/schedule`,
  directory: () => '/api/voice/directory',
  directoryId: (id: string) => `/api/voice/directory/${id}`,
  numbers: () => '/api/voice/phone-settings/numbers',
  numberRouting: (id: string) => `/api/voice/phone-settings/numbers/${id}/routing`,
  landlines: () => '/api/voice/landlines',
  landlineRegistration: () => '/api/voice/landlines/registration',
  landlineId: (id: string) => `/api/voice/landlines/${id}`,
  landlineNumbers: (id: string) => `/api/voice/landlines/${id}/numbers`,
  landlineCallerId: (id: string) => `/api/voice/landlines/${id}/caller-id`,
};

const unwrap = <T>(env: CsEnvelope<T>): T => env.data;

// ─── Agent flags (operations-native) ─────────────────────────────────────────

export const agentVoiceSettingsApi = {
  list: () => apiGet<VoiceAgentsResponse>(operationsClient, url.agentSettings()),
  update: (userId: string, flags: AgentVoiceFlags) =>
    apiPut<VoiceAgent, AgentVoiceFlags>(operationsClient, url.agentSetting(userId), flags),
};

// ─── Automation (CS-proxied) ─────────────────────────────────────────────────

export const automationApi = {
  get: () => apiGet<CsEnvelope<Automation>>(operationsClient, url.automation()).then(unwrap),
  update: (payload: UpdateAutomationPayload) =>
    apiPut<CsEnvelope<Automation>, UpdateAutomationPayload>(
      operationsClient,
      url.automation(),
      payload,
    ).then(unwrap),
};

// ─── AI receptionist (CS-proxied) ────────────────────────────────────────────

export const receptionistApi = {
  get: () =>
    apiGet<CsEnvelope<ReceptionistConfig>>(operationsClient, url.receptionist()).then(unwrap),
  update: (payload: UpdateReceptionistPayload) =>
    apiPut<CsEnvelope<ReceptionistConfig>, UpdateReceptionistPayload>(
      operationsClient,
      url.receptionist(),
      payload,
    ).then(unwrap),
};

// ─── IVR / call tree (CS-proxied) ────────────────────────────────────────────

export const ivrApi = {
  list: (all = false) =>
    apiGet<CsEnvelope<IvrTree[]>>(operationsClient, all ? `${url.ivr()}?all=true` : url.ivr()).then(
      unwrap,
    ),
  create: (payload: UpsertIvrPayload) =>
    apiPost<CsEnvelope<IvrTree>, UpsertIvrPayload>(operationsClient, url.ivr(), payload).then(
      unwrap,
    ),
  update: (id: string, payload: UpsertIvrPayload) =>
    apiPut<CsEnvelope<IvrTree>, UpsertIvrPayload>(operationsClient, url.ivrId(id), payload).then(
      unwrap,
    ),
  remove: (id: string) => apiDelete<CsEnvelope<unknown>>(operationsClient, url.ivrId(id)),
  activate: (id: string) =>
    apiPatch<CsEnvelope<IvrTree>, unknown>(operationsClient, url.ivrActivate(id), {}).then(unwrap),
  deactivate: (id: string) =>
    apiPatch<CsEnvelope<IvrTree>, unknown>(operationsClient, url.ivrDeactivate(id), {}).then(
      unwrap,
    ),

  /** Upload a greeting clip (.mp3/.wav); returns a stable public URL to store. */
  uploadAudio: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await operationsClient.post<{ url: string }>('/api/voice/ivr-audio', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
};

// ─── Directory: dialable extensions (CS-proxied) ─────────────────────────────

export const directoryApi = {
  list: () => apiGet<CsEnvelope<DirectoryEntry[]>>(operationsClient, url.directory()).then(unwrap),
  create: (payload: UpsertDirectoryPayload) =>
    apiPost<CsEnvelope<DirectoryEntry>, UpsertDirectoryPayload>(
      operationsClient,
      url.directory(),
      payload,
    ).then(unwrap),
  update: (id: string, payload: UpsertDirectoryPayload) =>
    apiPut<CsEnvelope<DirectoryEntry>, UpsertDirectoryPayload>(
      operationsClient,
      url.directoryId(id),
      payload,
    ).then(unwrap),
  remove: (id: string) => apiDelete<CsEnvelope<unknown>>(operationsClient, url.directoryId(id)),
};

// ─── Number assignment (CS-proxied) ──────────────────────────────────────────

export const numbersApi = {
  /**
   * Same rule as the landline list: a body without a `numbers` array is a
   * broken contract, not an account with no numbers, so it throws and the
   * section shows its retry state instead of "no numbers yet".
   */
  list: () =>
    apiGet<CsEnvelope<{ numbers: AssignableNumber[] }>>(operationsClient, url.numbers()).then(
      (env) => {
        const numbers = env?.data?.numbers;
        if (!Array.isArray(numbers)) {
          throw new ApiError(
            0,
            'UNKNOWN',
            'The calling service returned an unexpected number list.',
            env,
          );
        }
        return numbers;
      },
    ),
  /**
   * Keeps the envelope: the CS writes `message` for display ("This number will
   * ring only the assigned team members").
   */
  updateRouting: (id: string, patch: UpdateNumberRoutingPayload) =>
    apiPatch<
      CsEnvelope<{ _id: string; phoneNumber: string; numberRouting: NumberRouting }>,
      UpdateNumberRoutingPayload
    >(operationsClient, url.numberRouting(id), patch),
};

// ─── Landlines: desk phones / ATAs (CS-proxied) ──────────────────────────────

/**
 * Unlike the calls above, the mutations here keep the whole envelope. The CS
 * writes `message` for direct display ("This landline will ring for +1… only"),
 * and it reads better than anything we'd compose from the payload.
 */
export const landlinesApi = {
  /**
   * A missing or non-array `landlines` is a broken contract, NOT an account
   * with no handsets, so it throws rather than defaulting to `[]`.
   *
   * The distinction matters because the two render very differently: `[]` shows
   * "No desk phones yet", which tells the user every phone they registered has
   * been deleted. Throwing puts React Query into `isError`, where the section
   * shows its retry state instead. The operations proxy deliberately passes an
   * unrecognised body through untouched, so contract drift really can arrive
   * here as a 200 — this is the other half of that decision.
   */
  list: () =>
    apiGet<CsEnvelope<{ landlines: Landline[] }>>(operationsClient, url.landlines()).then((env) => {
      const landlines = env?.data?.landlines;
      if (!Array.isArray(landlines)) {
        throw new ApiError(
          0,
          'UNKNOWN',
          'The calling service returned an unexpected landline list.',
          env,
        );
      }
      return landlines;
    }),
  create: (payload: CreateLandlinePayload) =>
    apiPost<CsEnvelope<Landline>, CreateLandlinePayload>(
      operationsClient,
      url.landlines(),
      payload,
    ),
  /** An EMPTY array clears the restriction — the phone rings for every number. */
  setNumbers: (id: string, ringsForNumbers: string[]) =>
    apiPatch<CsEnvelope<LandlineRingScope>, { ringsForNumbers: string[] }>(
      operationsClient,
      url.landlineNumbers(id),
      { ringsForNumbers },
    ),
  /**
   * Which of the account's numbers this phone DIALS OUT AS — distinct from
   * `setNumbers`, which is what it answers for. Several phones may share one.
   */
  setCallerId: (id: string, phoneNumber: string) =>
    apiPatch<CsEnvelope<LandlineCallerId>, { phoneNumber: string }>(
      operationsClient,
      url.landlineCallerId(id),
      { phoneNumber },
    ),
  /** Whether each desk phone is signed in to Telnyx, keyed by landline id. */
  registration: () =>
    apiGet<CsEnvelope<{ registrations?: Record<string, LandlineRegistration> }>>(
      operationsClient,
      url.landlineRegistration(),
    ).then((env) => env?.data?.registrations ?? {}),
  /** The only way a landline comes off the account. */
  remove: (id: string) => apiDelete<CsEnvelope<unknown>>(operationsClient, url.landlineId(id)),
};

// ─── Account-wide phone hours (CS-proxied) ───────────────────────────────────

export const accountScheduleApi = {
  get: () =>
    apiGet<CsEnvelope<AccountCallSchedule>>(operationsClient, url.accountSchedule()).then(unwrap),
  update: (payload: UpdateAccountSchedulePayload) =>
    apiPut<CsEnvelope<AccountCallSchedule>, UpdateAccountSchedulePayload>(
      operationsClient,
      url.accountSchedule(),
      payload,
    ).then(unwrap),
};

// ─── IVR analytics (CS-proxied) ──────────────────────────────────────────────

export const ivrAnalyticsApi = {
  get: (range?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set('from', range.from);
    if (range?.to) qs.set('to', range.to);
    const query = qs.toString();
    return apiGet<CsEnvelope<IvrAnalytics>>(
      operationsClient,
      query ? `${url.ivrAnalytics()}?${query}` : url.ivrAnalytics(),
    ).then(unwrap);
  },
};

// ─── Call flows (CS-proxied) ─────────────────────────────────────────────────

export const callFlowsApi = {
  list: () => apiGet<CsEnvelope<CallFlow[]>>(operationsClient, url.callFlows()).then(unwrap),

  get: (id: string) =>
    apiGet<CsEnvelope<CallFlow>>(operationsClient, url.callFlow(id)).then(unwrap),

  create: (payload: UpsertCallFlowPayload) =>
    apiPost<CsEnvelope<CallFlow>, UpsertCallFlowPayload>(
      operationsClient,
      url.callFlows(),
      payload,
    ).then(unwrap),

  update: (id: string, payload: UpsertCallFlowPayload) =>
    apiPut<CsEnvelope<CallFlow>, UpsertCallFlowPayload>(
      operationsClient,
      url.callFlow(id),
      payload,
    ).then(unwrap),

  remove: (id: string) => apiDelete<CsEnvelope<unknown>>(operationsClient, url.callFlow(id)),

  /**
   * `replaceWith` names the step that takes over the callers who were heading
   * for the deleted one. The calling service REQUIRES it on a live flow, so
   * dropping it turns a legitimate delete into a refusal.
   */
  removeStep: (id: string, stepId: string, replaceWith?: string) =>
    apiDelete<CsEnvelope<CallFlow>>(
      operationsClient,
      replaceWith
        ? `${url.callFlowStep(id, stepId)}?replaceWith=${encodeURIComponent(replaceWith)}`
        : url.callFlowStep(id, stepId),
    ),

  // Their own verbs, because they are the only calls here that change what a
  // caller hears. Everything above edits a draft nobody is dialling.
  publish: (id: string) =>
    apiPost<CsEnvelope<CallFlow>, undefined>(
      operationsClient,
      url.callFlowPublish(id),
      undefined,
    ).then(unwrap),

  unpublish: (id: string) =>
    apiPost<CsEnvelope<CallFlow>, undefined>(
      operationsClient,
      url.callFlowUnpublish(id),
      undefined,
    ).then(unwrap),

  /** Go live at `publishAt` (ISO). The service re-checks everything then. */
  schedule: (id: string, publishAt: string) =>
    apiPost<CsEnvelope<CallFlow>, { publishAt: string }>(
      operationsClient,
      url.callFlowSchedule(id),
      { publishAt },
    ).then(unwrap),

  cancelSchedule: (id: string) =>
    apiDelete<CsEnvelope<CallFlow>>(operationsClient, url.callFlowSchedule(id)).then(unwrap),
};

// ─── Call sounds: played without exposing where they are stored ───────────────

/**
 * Which of the account's sounds to play. The `default-*` pair is Notifi's own
 * ring and hold music, used while the account has not uploaded its own.
 */
export type CallSoundSlot =
  | 'ringback'
  | 'hold'
  | 'greeting'
  | 'voicemail'
  | 'default-ringback'
  | 'default-hold';

const callSoundsApi = {
  /**
   * The sound's bytes, streamed by operations. Our default sounds and older
   * uploads live in our S3 bucket, and linking to them would put the bucket's
   * address in the page for any customer to read.
   */
  fetch: async (slot: CallSoundSlot): Promise<Blob> => {
    const res = await operationsClient.get<Blob>(
      `/api/voice/call-sounds/${encodeURIComponent(slot)}`,
      { responseType: 'blob' },
    );
    return res.data;
  },
};

export const voiceApi = {
  accountSchedule: accountScheduleApi,
  callSounds: callSoundsApi,
  agentSettings: agentVoiceSettingsApi,
  automation: automationApi,
  receptionist: receptionistApi,
  ivr: ivrApi,
  directory: directoryApi,
  numbers: numbersApi,
  landlines: landlinesApi,
  ivrAnalytics: ivrAnalyticsApi,
  callFlows: callFlowsApi,
};
